import React, { useEffect } from "react";
import dynamic from "next/dynamic";
import { useRouter, useSearchParams } from "next/navigation";
import { jsonLdScriptProps } from "react-schemaorg";

import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import getBrandPlacement, { Placement } from "@/api/requests/getBrandPlacement";
import { getDispensary } from "@/api/requests/getDispensary";
import { getDispensaryDeals } from "@/api/requests/getDispensaryDeals";
import getDispensaryReviews from "@/api/requests/getDispensaryReviews";
import getMenuItems, {
  GetMenuItemsAvailableFilters,
} from "@/api/requests/getMenuItems";
import { DISPENSARY_PAGES } from "@/constants/dispensary";
import { Deal } from "@/custom-types/Deals";
import { Dispensary, RetailType } from "@/custom-types/Dispensary";
import { MenuItem } from "@/custom-types/MenuItem";
import { DispensaryReview } from "@/custom-types/Reviews";
import useDomain from "@/hooks/useDomain";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { retailerMainMetaData } from "@/lib/retailerMetadata";
import { useDispatch } from "@/redux/hooks";
import {
  ComplianceRuleset,
  loadComplianceRuleset,
} from "@/redux/slices/complianceRules";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import isDispensaryDualLicense from "@/utils/dispensary/isDispensaryDualLicense";
import { getDispensaryUrl } from "@/utils/dispensaryUrls";
import { selectCategories } from "@/utils/filterUtils";
import generateDispensaryRedirect from "@/utils/generateDispensaryRedirect";
import { getMenuTypeCookie } from "@/utils/menuTypeUtils";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";
import generateDispensarySchema from "@/utils/schemas/generateDispensarySchema";
import { trackUserHistory } from "@/utils/userHistory";

import DispensaryBreadcrumbs from "@/components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHeader from "@/components/Dispensary/DispensaryHeader";
import DispensaryTabs from "@/components/Dispensary/DispensaryTabs";
import MenuNotAvailable from "@/components/Dispensary/MenuNotAvailable";
import PaidTierMenu from "@/components/Dispensary/PaidTierMenu";
import { ProductRecTrayData } from "@/components/Dispensary/ProductRecTrays/ProductRecTrays";

import MetaTags from "../../components/MetaTags";

const NearbyStores = dynamic(
  () => import("@/components/Dispensary/NearbyStores"),
);
const AboutDispensary = dynamic(
  () => import("@/components/Dispensary/AboutDispensary"),
);
const ReviewConfirmationModal = dynamic(
  () => import("@/components/Dispensary/ReviewsHeader/ReviewConfirmationModal"),
);

type MainPageProps = {
  complianceRuleset: ComplianceRuleset;
  brandPlacement: Placement | null;
  deals: Deal[];
  dispensary: Dispensary;
  productsForCategoryCarousels: ProductRecTrayData[];
  staffPicks: MenuItem[];
  ratingsWithoutContent: number;
  reviews: DispensaryReview[];
  totalReviews: number;
  availableFilters: GetMenuItemsAvailableFilters;
  totalItems: number;
};

const MainPage: React.FC<MainPageProps> = ({
  complianceRuleset,
  brandPlacement,
  deals,
  dispensary,
  productsForCategoryCarousels,
  ratingsWithoutContent,
  reviews,
  totalReviews,
  availableFilters,
  totalItems,
  staffPicks,
}) => {
  const {
    id,
    city,
    coverPhotoUrl,
    featureTier,
    hasMenuItems,
    logoUrl,
    name,
    retailType,
    slug,
    state,
  } = dispensary;
  const baseUrl = useDomain();
  const dispatch = useDispatch();

  const isInfoTier = featureTier === "info";

  useEffect(() => {
    if (complianceRuleset) {
      dispatch(loadComplianceRuleset(complianceRuleset));
    }
  }, []);

  useEffect(() => {
    trackUserHistory({
      resourceId: id,
      slug,
      type: "dispensary",
    });
  }, [id]);
  const router = useRouter();
  const params = useSearchParams();
  const countryCode = useDomainCountryCode();

  const showConfirmationModal = !!params?.get("show_confirmation_modal");
  const showFavoriteDispensaryMessage = !!params?.get("show_favorite_message");

  const ssoMenuRedirect = () => {
    router.push(
      getDispensaryUrl({
        countryCode,
        slug,
        tab: "menu",
      }),
    );
  };

  const ssoDispensaryRedirect = () => {
    router.push(
      getDispensaryUrl({
        countryCode,
        slug,
      }),
    );
  };

  const { description, image, title } = retailerMainMetaData(
    city,
    countryCode,
    coverPhotoUrl,
    logoUrl,
    name,
    retailType as RetailType,
    state,
  );

  return (
    <>
      <MetaTags
        description={description}
        image={image}
        shouldShowAlternates={false}
        title={title}
      >
        <script
          {...jsonLdScriptProps(
            generateDispensarySchema(
              dispensary,
              baseUrl,
              dispensary.country || countryCode,
              reviews,
              productsForCategoryCarousels,
            ),
          )}
        />
      </MetaTags>
      <div className="flex flex-col gap-xl">
        <div>
          <DispensaryHeader dispensary={dispensary} isMainPage={true} />
          <DispensaryTabs
            activeTab={DISPENSARY_PAGES.main}
            dispensary={dispensary}
          />
        </div>
        <div className="flex flex-col gap-xxl">
          {!isInfoTier && hasMenuItems ? (
            <PaidTierMenu
              dispensary={dispensary}
              brandPlacement={brandPlacement}
              deals={deals}
              productsForCategoryCarousels={productsForCategoryCarousels}
              availableFilters={availableFilters}
              totalItems={totalItems}
              staffPicks={staffPicks}
            />
          ) : (
            <>
              <MenuNotAvailable
                dispensaryFeatureTier={dispensary.featureTier}
                dispensaryId={dispensary.id}
                dispensaryName={dispensary.name}
                dispensaryRetailType={dispensary.retailType}
              />
              {isInfoTier && (
                <NearbyStores
                  dispensaryId={dispensary.id}
                  locations={dispensary.locations}
                  locationPaths={dispensary.locationPaths}
                  className="container mt-xl md:mt-[40px] mb-lg"
                />
              )}
            </>
          )}
          <AboutDispensary
            dispensary={dispensary}
            ratingsWithoutContent={ratingsWithoutContent}
            reviews={reviews}
            totalReviews={totalReviews}
          />
          {showConfirmationModal && (
            <ReviewConfirmationModal
              dispensaryId={dispensary.id}
              favorite={showFavoriteDispensaryMessage}
              ssoMenuRedirect={ssoMenuRedirect}
              ssoDispensaryRedirect={ssoDispensaryRedirect}
            />
          )}
          <DispensaryBreadcrumbs dispensary={dispensary} />
        </div>
      </div>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<MainPageProps>(
  async (context) => {
    const { query, req, resolvedUrl } = context;
    const { slug, menu_type } = query;

    const dispensary = await throwOnRedirectHint(
      () => getDispensary(slug as string),
      slug as string,
    );

    const domainCountryCode = getCountryCodeHeader(req.headers);

    if (resolvedUrl.startsWith("/cbd-store") && domainCountryCode !== "US") {
      return {
        notFound: true,
      };
    }

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const userMedRecPreference = menu_type
      ? Array.isArray(menu_type)
        ? String(menu_type)
        : menu_type
      : getMenuTypeCookie(req);
    const isDualLicenseDispensary = isDispensaryDualLicense(
      dispensary.tags || [],
    );

    const menuItemsCall = getMenuItems(slug as string, {
      enableNewFilters: true,
      menu_type: isDualLicenseDispensary ? userMedRecPreference : undefined,
    });

    const complianceCall = getRegionComplianceRules(
      dispensary.state,
      dispensary.country,
    );

    const brandPlacementCall = getBrandPlacement(dispensary.slug, {
      menuType: isDualLicenseDispensary ? userMedRecPreference : undefined,
      strategies: ["lite-amplification", "menu-merchandising"],
    });

    const dealsCall = getDispensaryDeals(dispensary.slug);

    const staffPicksCall = getMenuItems(slug as string, {
      is_staff_pick: true,
      menu_type: isDualLicenseDispensary ? userMedRecPreference : undefined,
    });

    const reviewsCall = getDispensaryReviews(dispensary.slug, 0, 4);

    const [
      menuData,
      complianceRuleset,
      brandPlacement,
      { data: deals },
      { menuItems: staffPicks },
      { ratingsWithoutContent, reviews, totalReviews },
    ] = await Promise.all([
      menuItemsCall,
      complianceCall,
      brandPlacementCall,
      dealsCall,
      staffPicksCall,
      reviewsCall,
    ]);

    const categories = selectCategories(menuData.availableFilters);

    // Temporary: We will create a new endpoint that will simplify the multiple calls to
    // getMenuItems down to one call.
    const productsForCategoryCarousels = await Promise.all(
      // Make menu item requests for each category
      categories.map((category) =>
        getMenuItems(dispensary.slug, {
          is_staff_pick: false,

          menu_type: isDualLicenseDispensary ? userMedRecPreference : undefined,

          perPage: 8,

          product_category: [category],
          /**
           * Canadian brands are not currently offered the lite-amplification
           * product that drives this promotional placement, but there is not
           * currently a way to differentiate whether to show this placement
           * depending on whether a brand has been sold this functionality or
           * not. When the integration of brand accounts in salesforce is
           * complete, this can be removed.
           *
           * Tracking ticket: https://leafly.atlassian.net/browse/BRND-419
           */
          promotedSlots: domainCountryCode === "CA" ? 0 : 1,
        }),
      ),
    )
      .then((menuItemsByCategory) =>
        menuItemsByCategory.map(({ menuItems }, i) => {
          return {
            menuItems,
            name: categories[i],
          };
        }),
      )
      .catch(() => []);

    return {
      props: {
        availableFilters: menuData.availableFilters,
        brandPlacement,
        complianceRuleset,
        deals,
        dispensary,
        productsForCategoryCarousels,
        ratingsWithoutContent,
        reviews,
        staffPicks,
        totalItems: menuData.totalItems,
        totalReviews,
      },
    };
  },
);

export default MainPage;
